import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isEqual, uniq } from 'lodash';
import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import {
  DisconnectedIllustration,
  EditWorkflowType,
  FlowBuilder2FlowFieldNamesEnum,
  FlowResourceType,
  getResponseErrorMessage,
  useAssistantRequest,
  useEditorStore,
  useNameField,
  useAssistantLoadingStore,
  useDragAndDropScroll,
} from '@diana/core';
import { useTranslation } from 'react-i18next';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { FlowNameField } from '../../Components/FlowNameField/FlowNameField';
import { NameFieldWrapper } from '../../Components/NameFieldWrapper/NameFieldWrapper';
import { useEntityFieldState } from '../../Support/useEntityFieldState';
import { AssetTaskBuilder } from './TaskBuilder/AssetTaskBuilder';
import { StepBuilder } from './StepBuilder/StepBuilder';
import { FlowIconSelector } from '../../Components/FlowIconSelector/FlowIconSelector';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { FocusWrapper } from '../../FlowBuilder2Page.styles';
import { AcceptRejectBar } from '../../Components/EditAssistant/AcceptRejectBar';
import { scrollToSection, scrollToTask } from '../../Support/useBuilderScroll';

export const Content = memo(() => {
  const { typography, palette } = useTheme();
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const inputRef = useRef<HTMLInputElement>(null);

  const { isLoading, error: pageError } = useFlowBuilder2PageContext();

  const flowResourceType = useEditorStore((s) => s.flow?.properties.flowResourceType);
  const firstTaskId = useEditorStore((s) => s.flow?.steps[0]?.taskId ?? '');
  const firstLayoutId = useEditorStore((s) => s.flow?.steps[0]?.layoutId ?? '');
  const stepIds = useEditorStore((s) => uniq(s.flow?.steps.map((s) => s.stepId) ?? []), isEqual);
  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const flowDisplayName = useEditorStore((s) => s.flow?.properties.displayName);

  const resetActiveContext = useFlowBuilder2PageStore((s) => s.resetActiveContext);
  const hasActiveTask = useFlowBuilder2PageStore((s) => !!s.activeTaskId);
  const hasActiveComponent = useFlowBuilder2PageStore((s) => !!s.activeComponent);
  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const flowPendingStatus = useFlowBuilder2PageStore((s) => s.aiPendingStatuses.flows);
  const setIsScrollingDndDragItem = useFlowBuilder2PageStore((s) => s.setIsScrollingDndDragItem);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const [isAutoFocusNameField, setIsAutoFocusNameField] = useState(false);

  useDragAndDropScroll({
    disableLeftDrawerArea: true,
    onScrollStart: () => setIsScrollingDndDragItem(true),
    onScrollEnd: () => setIsScrollingDndDragItem(false),
  });

  const disabled = hasAnyPendingStatus || isChatLoading;
  const isFlowPendingStatus = flowPendingStatus.includes(flowId);
  const isFlowActive = !hasActiveTask && !hasActiveComponent;
  const isEntityResourceType = flowResourceType === FlowResourceType.Entity;
  const placeholder = isEntityResourceType
    ? t('labels.untitledAssetType')
    : t('labels.untitledWorkflow');

  const storeParams = {
    type: isEntityResourceType ? EditWorkflowType.Layout : EditWorkflowType.Flow,
    flowId,
    layoutId: isEntityResourceType ? firstLayoutId : undefined,
    componentId: undefined,
  };

  const fieldState = useEntityFieldState({
    objectId: flowId,
    objectType: 'flow',
    fieldName: FlowBuilder2FlowFieldNamesEnum.DisplayName,
  });
  const isDisplayNameError = fieldState.validationResult?.type === 'error';

  const renameFlow = useCallback(() => {
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const length = el.value.length;
        el.setSelectionRange(length, length);
      }
    }, 0);
  }, []);

  const titleFontSize = '18px';

  const {
    onClick: handleLabelClick,
    onDoubleClick: handleLabelDoubleClick,
    onFocus,
    onBlur,
    onKeyDown,
    isRenaming,
  } = useNameField({
    onClick: resetActiveContext,
    onDoubleClick: renameFlow,
    isError: isDisplayNameError,
  });

  const { hasRequest } = useAssistantRequest(storeParams);

  useEffect(() => {
    const { activeTaskId, activeComponent } = useFlowBuilder2PageStore.getState();
    if (!!activeTaskId || !!activeComponent) {
      !!activeComponent
        ? setTimeout(() => scrollToSection(activeComponent.componentId), 500)
        : setTimeout(() => scrollToTask(activeTaskId ?? ''), 500);
    }
  }, []);

  const content = useMemo(() => {
    if (isLoading || hasRequest)
      return (
        <Stack gap={2} data-testid="builder-workflow-skeleton-loader">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={`builder-content-skeleton-${i}`} variant="rounded" height={140} />
          ))}
        </Stack>
      );

    if (pageError)
      return (
        <Stack alignItems="center" gap={2} p={5}>
          <DisconnectedIllustration />
          <Typography>{getResponseErrorMessage(pageError)}</Typography>
        </Stack>
      );

    if (isEntityResourceType && firstTaskId)
      return <AssetTaskBuilder taskId={firstTaskId} stepId={stepIds[0]} flowId={flowId} />;

    return (
      <Stack gap={1}>
        {stepIds.map((stepId) => (
          <StepBuilder key={stepId} stepId={stepId} flowId={flowId} />
        ))}
      </Stack>
    );
  }, [firstTaskId, isEntityResourceType, isLoading, pageError, flowId, stepIds, hasRequest]);

  useLayoutEffect(() => {
    if (flowId && !flowDisplayName) setIsAutoFocusNameField(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId]);

  return (
    <Box
      data-testid="LayoutViewContent"
      onClick={resetActiveContext}
      sx={{
        position: 'relative',
        cursor: hasActiveTask || hasActiveComponent ? 'pointer' : 'auto',
        zIndex: 3,
      }}
    >
      <FocusWrapper
        isActive={isFlowActive && !isLoading && !hasRequest && !!flowId}
        isPendingStatus={
          (isEntityResourceType && hasAnyPendingStatus) ||
          (isFlowPendingStatus && !isLoading && !hasRequest && !!flowId)
        }
        sx={{ p: 2 }}
      >
        <Stack direction="row" alignItems="center" gap={1} mb={3} sx={{ height: 40 }}>
          <Box onClick={(e) => e.stopPropagation()}>
            <FlowIconSelector hasWorkflowRequest={hasRequest} />
          </Box>
          {isRenaming || isLoading ? (
            <FlowNameField
              data-flow-name-field
              fullWidth
              multiline
              variant="standard"
              autoFocus={isAutoFocusNameField}
              disabled={hasRequest}
              placeholder={placeholder}
              fontSize={titleFontSize}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onClick={(e) => e.stopPropagation()}
              inputRef={inputRef}
              InputProps={{ sx: { p: 0 } }}
              inputProps={{
                sx: {
                  fontSize: titleFontSize,
                  width: '100%',
                  borderRadius: 1.5,
                  backgroundColor: palette.background.card,
                },
              }}
              sx={{
                top: 6,
                minWidth: '250px',
                '.MuiInput-root:before': { borderColor: 'transparent' },
                '.MuiInputBase-input, .MuiInputBase-input::placeholder': {
                  fontWeight: typography.fontWeightMedium,
                },
              }}
            />
          ) : (
            <NameFieldWrapper
              disabled={disabled || hasRequest}
              onDoubleClick={handleLabelDoubleClick}
              onClick={(e) => {
                e.stopPropagation();
                handleLabelClick(e);
              }}
            >
              <Typography
                sx={{
                  ml: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 250,
                  color: !flowDisplayName ? palette.text.placeholder : palette.text.primary,
                  fontWeight: 500,
                  fontSize: 18,
                }}
              >
                {!flowDisplayName ? placeholder : flowDisplayName}
              </Typography>
            </NameFieldWrapper>
          )}
        </Stack>
        {content}
      </FocusWrapper>
      <Box sx={{ height: '25px' }} />
      {hasAnyPendingStatus && !isLoading ? <AcceptRejectBar /> : null}
    </Box>
  );
}, isEqual);
